// Shared formatter helpers for weekly-bucket x-axes across metric-view charts.
//
// Two date conventions show up in this module:
//   - `bucketStart` strings are the Monday of an ISO week, e.g. "2026-05-18".
//     These are emitted by the backend (`DATE_TRUNC('week', …)`) and by the
//     frontend `weekly-bucketing` helper.
//   - `today` is whatever the caller hands us (usually `todayIsoUtc()`).
//
// The current week's tick is relabeled as a range (`5/18 – 21`) so users
// reading the chart on Thursday don't perceive the Monday-labeled bar as
// stale data. All other ticks stay as single-date M/D strings.

import { toDisplayString } from '@/lib/utils';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseIso(iso: string): { y: string; m: number; d: number } | null {
  const match = ISO_DATE.exec(iso);
  if (!match) return null;
  return { y: match[1], m: Number(match[2]), d: Number(match[3]) };
}

/**
 * Reduce an ISO date to `M/D` — e.g. `"2026-05-18"` → `"5/18"`. Non-ISO inputs
 * pass through unchanged (covers the legacy "Q1" / empty-string callers in
 * `metric-card-sparkline`).
 */
export function formatBucketTick(value: string): string {
  const parsed = parseIso(value);
  return parsed ? `${parsed.m}/${parsed.d}` : value;
}

/**
 * Render `[start, end]` as a compact range:
 *   - Same month + year → `"5/18 – 21"`
 *   - Otherwise         → `"5/30 – 6/2"` or `"12/29 – 1/4"`
 * Inputs that fail the ISO regex round-trip as `"<a> – <b>"`.
 */
export function formatBucketRange(startIso: string, endIso: string): string {
  const start = parseIso(startIso);
  const end = parseIso(endIso);
  if (!start || !end) return `${startIso} – ${endIso}`;
  const sameMonth = start.y === end.y && start.m === end.m;
  if (sameMonth) {
    return `${start.m}/${start.d} – ${end.d}`;
  }
  return `${start.m}/${start.d} – ${end.m}/${end.d}`;
}

function isoMondayOf(iso: string): string {
  const parsed = parseIso(iso);
  if (!parsed) return iso;
  const d = new Date(Date.UTC(Number(parsed.y), parsed.m - 1, parsed.d));
  const dow = d.getUTCDay(); // 0=Sun..6=Sat
  const daysFromMonday = (dow + 6) % 7;
  d.setUTCDate(d.getUTCDate() - daysFromMonday);
  return d.toISOString().slice(0, 10);
}

/** Today as `YYYY-MM-DD` in UTC. Matches the convention used by `filters.to`
 *  for rolling pills (see `management-date-presets.getTodayIso`). */
export function todayIsoUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface IBucketAxisInput {
  /** Current date as `YYYY-MM-DD` (UTC). */
  today: string;
  /** Filter range start (inclusive). */
  rangeFrom: string;
  /** Filter range end (inclusive). */
  rangeTo: string;
}

export interface IBucketAxisProps {
  /** `tickFormatter` for `<XAxis>` — relabels only the current bucket. */
  tickFormatter: (value: string) => string;
  /** Data value for `<ReferenceLine x={…}>`. `null` when today ∉ range. */
  todayLineDate: string | null;
  /** `labelFormatter` for `<Tooltip>` — appends "(so far)" on the current
   *  bucket, M/D'd otherwise. */
  tooltipLabelFormatter: (label: unknown) => string;
}

export function buildBucketAxisProps({
  today,
  rangeFrom,
  rangeTo,
}: Readonly<IBucketAxisInput>): IBucketAxisProps {
  const todayInRange = today >= rangeFrom && today <= rangeTo;
  const todayMonday = todayInRange ? isoMondayOf(today) : null;
  const todayIsMonday = todayMonday !== null && todayMonday === today;

  const tickFormatter = (value: string): string => {
    if (todayMonday !== null && value === todayMonday && !todayIsMonday) {
      return formatBucketRange(todayMonday, today);
    }
    return formatBucketTick(value);
  };

  const tooltipLabelFormatter = (label: unknown): string => {
    const v = toDisplayString(label);
    if (todayMonday !== null && v === todayMonday && !todayIsMonday) {
      return `${formatBucketTick(todayMonday)} – ${formatBucketTick(today)} (so far)`;
    }
    return formatBucketTick(v);
  };

  return { tickFormatter, todayLineDate: todayMonday, tooltipLabelFormatter };
}
